import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import _, { debounce } from 'lodash';
import { init, EChartsOption, ECharts, SeriesOption } from 'echarts';
import { useStore } from 'app/context';
import 'echarts-extension-gmap';
import React from 'react';
import { MapInfo } from 'app/components/maps/MapInfo';
import { MapPoint, MapPointGroup } from 'app/stores/ui/agentur.maps.ui.store';

interface IBaseMap {
	series: DvsSeriesOption[];
	showInfoWindow?: boolean;
	vmInfoComponent?: React.ReactElement<{ agtId: number }>;
	itemCountOn?: string;
	pinGroupOn?: string;
	onZoom?: (series: DvsSeriesOption[], z: number) => void;
}

export type DvsSeriesOption = SeriesOption & {
	showAgtLabel?: boolean;
	dvsData?: MapPoint[];
};

export const EchartMap = observer((props: IBaseMap) => {
	const { uiStore, agenturMapsUiStore } = useStore();
	const { showInfoWindow = true } = props;
	const chartRef = useRef<HTMLDivElement>(null);
	// let chart: ECharts | null = null;
	const [chart, setChart] = useState<ECharts | null>(null);
	const [map, setMap] = useState<any | null>(null);
	const [mapsReady, setMapsReady] = useState(false);

	useEffect(() => {
		agenturMapsUiStore.mapsStore.ensureLoaded().then((ok) => {
			if (ok) {
				setMapsReady(true);
			}
		});
	}, [agenturMapsUiStore.mapsStore]);

	useEffect(() => {
		if (chartRef.current !== null && mapsReady) {
			setChart(init(chartRef.current, { width: '100%', height: '100%' }));
		}
		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);

		window.addEventListener('resize', handleResize);
		return () => {
			chart?.dispose();
			window.removeEventListener('resize', handleResize.cancel);
		};
	}, [chartRef, uiStore.printMode, mapsReady]);

	useEffect(() => {
		if (!chart) {
			return;
		}

		const mapSettings = agenturMapsUiStore.mapsStore.getMapSettings();
		let option: EChartsOption = {
			legend: {
				show: true, // Display the legend
				orient: 'horizontal', // Orientation: 'horizontal' or 'vertical'
				left: 'center', // Position of the legend
				top: 'top', // Adjust as needed
				backgroundColor: '#ffffff', // Set white background for the legend box
				borderColor: '#ccc', // Optional: Add a border color
				borderWidth: 1, // Optional: Set border width,
				padding: 20,
			},

			gmap: {
				// initial options of Google Map
				// See https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions for details
				// initial map center, accepts an array like [lng, lat] or an object like { lng, lat }
				// center: [10.2656, 51.0939,],
				center: [mapSettings.lng, mapSettings.lat],
				zoom: mapSettings.zoom,
				mapId: mapSettings.mapId,

				renderOnMoving: true,
				// the zIndex of echarts layer for Google Map. `2000` by default.
				echartsLayerZIndex: 3,
				roam: true,
				height: '100%', // Set the height of the Google Map container#
				width: '100%',
			},
			animation: false,
			coordinateSystem: 'gmap', // Ensure 'echarts-extension-gmap' is properly initialized
		};
		chart.setOption(option);

		// @ts-ignore
		const gmap = chart.getModel().getComponent('gmap').getGoogleMap();

		gmap.addListener('tilesloaded', () => {
			setMap(gmap);
		});
		gmap.addListener('bounds_changed', () => {
			agenturMapsUiStore.mapsStore.saveMapSettings(gmap);
			// chart.renderToCanvas();
		});
		const zoomListener = gmap.addListener('zoom_changed', () => {
			agenturMapsUiStore.mapsStore.saveMapSettings(gmap);
			configureSeries(gmap);
			setTimeout(() => {
				chart.setOption({ series: props.series });
				if (props.onZoom) {
					const s = props.onZoom(props.series, gmap.getZoom());
					configureSeries(gmap);
					chart.setOption({ series: s });
					chart.renderToCanvas();
				}
			}, 60);
		});

		chart.on('click', 'series', (x: any) => {
			agenturMapsUiStore.openActiveAgts(gmap, [x.data[0], x.data[1]]);
		});

		return () => {
			if (zoomListener) {
				zoomListener.remove();
			}
		};
	}, [chart]);

	const groupMapPoints = (map: google.maps.Map, points: MapPoint[]) => {
		console.time('groupy');
		let thresholdPx = 20; // in pixels
		const projection = map.getProjection()!;
		const zoom = map.getZoom()!;
		if (zoom > 5) {
			thresholdPx = 40;
		}
		if (zoom > 6) {
			thresholdPx = 60;
		}
		if (zoom > 7) {
			thresholdPx = 80;
		}
		if (zoom > 8) {
			thresholdPx = 80;
		}
		if (zoom > 9) {
			thresholdPx = 100;
		}

		const groups: MapPointGroup[] = [];
		for (const point of points) {
			const pixel = projection!.fromLatLngToPoint(new google.maps.LatLng(point.lat!, point.lng));
			let added = false;
			for (const group of groups) {
				const groupPixel = projection.fromLatLngToPoint(new google.maps.LatLng(group.lat!, group.lng));
				const dx = pixel!.x - groupPixel!.x;
				const dy = pixel!.y - groupPixel!.y;
				const distPx = Math.sqrt(dx * dx + dy * dy) * Math.pow(2, zoom!);
				if (distPx < thresholdPx) {
					group.points.push(point);
					added = true;
					break;
				}
			}
			if (!added) {
				const group = new MapPointGroup(point.lat!, point.lng!);
				group.points.push(point);
				groups.push(group);
			}
		}
		console.timeEnd('groupy');
		return groups;
	};

	const configureSeries = (map: any) => {
		props.series.forEach((s) => {
			if (Array.isArray(s.dvsData)) {
				const groupedData = groupMapPoints(map, s.dvsData!);
				s.data = groupedData!.map((x) => x.asArray);
				// if (s.showAgtLabel) {
				//     s.emphasis = {
				//         // itemStyle: {
				//         //     // Color in emphasis state.
				//         //     color: 'red'
				//         // },
				//         label: {
				//             show: true,
				//             // Text in emphasis.
				//             formatter: (p: any) => {
				//                 const lng = p.data[0];
				//                 const lat = p.data[1];

				//                 const grp = groupedData.find(x => x.lat === lat && x.lng === lng);
				//                 if (grp && grp.points.length > 0) {
				//                     return grp.points.map(x => x.agt.agtId + ' ' + x.agt.name).join('\n')
				//                 }
				//                 if (grp && grp.points.length === 1) {
				//                     return grp.points[0].agt.agtId + ' ' + grp.points[0].agt.name;
				//                 }
				//                 return '';
				//             },
				//             color: 'black',
				//             backgroundColor: '#ffffff',

				//         },
				//     }
				// }
				if (s.name === props.itemCountOn) {
					// @ts-ignore
					s.label = {
						show: true,
						color: '#fff',
						// backgroundColor: '#ffffff',
						formatter: function (j: any) {
							const groupCount = j.data[2];
							if (groupCount < 100000) {
								if (groupCount === 1) {
									return '';
								}
								return groupCount;
							}
						},
						minMargin: 0,
					};
				}
			}
		});
	};

	useEffect(() => {
		if (!chart || !map) {
			return;
		}
		configureSeries(map);
		let series = props.series;
		if (props.onZoom) {
			props.onZoom(props.series, map.getZoom());
		}
		chart.setOption({
			series: series,
		});
		console.log('setting data');
	}, [chart, props.series, map]);

	if (agenturMapsUiStore.mapsStore.loadFailed) {
		return <div className="pad-1rem">Die Karte konnte nicht geladen werden, da Google Maps nicht erreichbar ist. Bitte prüfen Sie Ihre Internetverbindung und laden Sie die Seite neu.</div>;
	}

	return (
		<>
			<div style={{ width: '100%', height: '100%', background: '#ffffff', position: 'relative' }} ref={chartRef}></div>
			{showInfoWindow && <MapInfo vmInfoComponent={props.vmInfoComponent} />}
		</>
	);
});
